import { icReferenceTypes, icReferenceUnavailable } from '../ic-reference-availability';
import { icStatisticalConfigurationValidator } from '../ic-statistical-validation';
import { icDecisionBasis } from '../ic-decision-basis';
import { icRepeatabilityPreview } from '../ic-repeatability-contract';
import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AlertifyService } from 'src/app/Service/Alertify.service';
import { ICService } from 'src/app/Service/IC/IC.service';
import { UnitService } from 'src/app/Service/Unit/Unit.service';
import { Unit_Interface } from 'src/app/Interface/Unit_Interface';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-ic-template-edit',
  templateUrl: './ICTemplateEdit.component.html',
  styleUrls: ['./ICTemplateEdit.component.css']
})
export class ICTemplateEditComponent implements OnInit {
  readonly decisionBasis = icDecisionBasis;
  readonly repeatabilityPreview = icRepeatabilityPreview;
  form: FormGroup;
  saving = false;
  loading = false;
  units: Unit_Interface[] = [];
  unitLoadError = '';
  unitsLoaded = false;

  readonly resultTypes = [
    { value: 'CHECK', label: 'کنترل انطباق' }, { value: 'NUMERIC', label: 'اندازه‌گیری عددی' },
    { value: 'NUMERIC_WITH_REFERENCE', label: 'اندازه‌گیری عددی با مرجع' }, { value: 'REPEATABILITY', label: 'تکرارپذیری' },
    { value: 'PASS_FAIL', label: 'کنترل انطباق دوحالته' }, { value: 'TEXT', label: 'مشاهده متنی' }, { value: 'TRUENESS_BIAS', label: 'ارزیابی صحت / اریبی' }
  ];
  readonly acceptanceTypes = [
    { value: 'MAX', label: 'حداکثر مجاز' }, { value: 'MIN', label: 'حداقل مجاز' }, { value: 'RANGE', label: 'محدوده مجاز' },
    { value: 'ABS_DIFFERENCE', label: 'اختلاف مطلق مجاز' }, { value: 'PERCENT_DIFFERENCE', label: 'اختلاف درصدی مجاز' },
    { value: 'STD_DEV', label: 'انحراف معیار مجاز' }, { value: 'MANUAL', label: 'سازگاری مترولوژیک / غیرعددی' }, { value: 'STATISTICAL_TRUENESS', label: 'صحت مترولوژیک (آمار تکمیلی)' }
  ];
  readonly referenceTypes = icReferenceTypes;
  readonly referenceUnavailable = icReferenceUnavailable;
  readonly formulaTypes = [
    { value: 'NONE', label: 'بدون فرمول عددی' }, { value: 'DIFFERENCE', label: 'اختلاف با مرجع' }, { value: 'ABS_DIFFERENCE', label: 'اختلاف مطلق' },
    { value: 'PERCENT_DIFFERENCE', label: 'اختلاف درصدی' }, { value: 'MEAN', label: 'میانگین' }, { value: 'STD_DEV', label: 'انحراف معیار نمونه' },
    { value: 'RANGE', label: 'دامنه قرائت‌ها' }, { value: 'TRUENESS_BIAS', label: 'ارزیابی صحت / اریبی' }
  ];

  constructor(private fb: FormBuilder, private service: ICService, private unitService: UnitService, private router: Router, private route: ActivatedRoute, private alert: AlertifyService) {
    this.form = this.buildForm();
  }

  ngOnInit(): void {
    this.loadUnits();
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!Number.isFinite(id) || id <= 0) { this.alert.ErrorMessage('شناسه الگو معتبر نیست'); return; }
    this.loading = true;
    this.service.getTemplate(id).pipe(finalize(() => this.loading = false)).subscribe({
      next: x => { this.form.patchValue({ id: x.id, previous_IC_Template_ID: x.previous_IC_Template_ID, iC_Code: x.iC_Code, iC_Title: x.iC_Title, description: x.description, version: x.version, status: x.status, time_Estimated: x.time_Estimated }); x.items.forEach(item => this.addItem(item)); if (!this.items.length) this.addItem(); this.form.markAsPristine(); },
      error: e => this.alert.ErrorMessage(e?.error?.message || 'دریافت الگو با خطا مواجه شد')
    });
  }

  loadUnits(): void {
    this.unitLoadError = '';
    this.unitsLoaded = false;
    this.unitService.GetRecord().subscribe({
      next: units => { this.units = (units || []).filter(unit => unit.del !== true); this.unitsLoaded = true; },
      error: () => { this.units = []; this.unitLoadError = 'دریافت فهرست واحدها ناموفق بود.'; }
    });
  }

  hasActiveUnit(id: number | null): boolean { return this.units.some(unit => unit.id === id); }

  get items(): FormArray { return this.form.get('items') as FormArray; }

  private buildForm(): FormGroup {
    return this.fb.group({
      id: [0], previous_IC_Template_ID: [null], iC_Code: ['', Validators.required], iC_Title: ['', Validators.required],
      description: [''], version: [1, [Validators.required, Validators.min(1)]], status: [true], time_Estimated: [null, Validators.min(0)],
      createNewVersion: [false], items: this.fb.array([])
    });
  }

  addItem(source?: any): void {
    this.items.push(this.itemGroup(source));
    this.reorder();
  }

  removeItem(index: number): void { this.items.removeAt(index); this.reorder(); }

  moveUp(index: number): void {
    if (index <= 0) return;
    const value = this.items.at(index);
    this.items.removeAt(index);
    this.items.insert(index - 1, value);
    this.reorder();
  }

  moveDown(index: number): void {
    if (index >= this.items.length - 1) return;
    const value = this.items.at(index);
    this.items.removeAt(index);
    this.items.insert(index + 1, value);
    this.reorder();
  }

  reorder(): void {
    this.items.controls.forEach((control, index) => {
      control.patchValue({ order_Item: index + 1, display_Order: index + 1 }, { emitEvent: false });
    });
  }

  private itemGroup(source?: any): FormGroup {
    const group = this.fb.group({
      id: [0], order_Item: [0], title: ['', Validators.required], description: [''], result_Type: ['CHECK', Validators.required], unit_ID: [null],
      period_Days: [180, [Validators.required, Validators.min(1)]], warning_Days: [15, [Validators.required, Validators.min(0)]], number_Of_Readings: [null, Validators.min(1)],
      reference_Type: ['FIXED', Validators.required], reference_Value: [null], acceptance_Type: ['MANUAL', Validators.required], lower_Limit: [null], upper_Limit: [null],
      max_Allowed_Difference: [null], measurement_Standard_Uncertainty: [null], reference_Standard_Uncertainty: [null], coverage_Factor: [2, Validators.min(0.000001)],
      formula_Type: ['NONE', Validators.required], formula_Expression: [''], instruction: ['', Validators.required], acceptance_Criteria: ['', Validators.required], mandatory: [true], active: [true], display_Order: [0]
    }, { validators: icStatisticalConfigurationValidator });
    if (source) group.patchValue(source, { emitEvent: false });
    return group;
  }

  configureResultType(item: FormGroup): void {
    const type = item.get('result_Type')?.value;
    const defaults: any = {
      CHECK: { acceptance_Type: 'MANUAL', formula_Type: 'NONE', number_Of_Readings: null },
      PASS_FAIL: { acceptance_Type: 'MANUAL', formula_Type: 'NONE', number_Of_Readings: null },
      TEXT: { acceptance_Type: 'MANUAL', formula_Type: 'NONE', number_Of_Readings: null },
      NUMERIC: { acceptance_Type: 'RANGE', formula_Type: 'NONE', number_Of_Readings: null },
      NUMERIC_WITH_REFERENCE: { acceptance_Type: 'ABS_DIFFERENCE', formula_Type: 'DIFFERENCE', number_Of_Readings: null },
      REPEATABILITY: { acceptance_Type: 'STD_DEV', formula_Type: 'STD_DEV', number_Of_Readings: 5 },
      TRUENESS_BIAS: { acceptance_Type: 'MANUAL', formula_Type: 'TRUENESS_BIAS', number_Of_Readings: 5 }
    };
    if (defaults[type]) item.patchValue(defaults[type]);
  }

  isEvaluated(item: FormGroup): boolean { return !['CHECK', 'PASS_FAIL', 'TEXT'].includes(item.get('result_Type')?.value); }
  usesReference(item: FormGroup): boolean { return ['NUMERIC_WITH_REFERENCE', 'TRUENESS_BIAS'].includes(item.get('result_Type')?.value); }
  usesReadings(item: FormGroup): boolean { return ['REPEATABILITY', 'TRUENESS_BIAS'].includes(item.get('result_Type')?.value) || (item.get('result_Type')?.value === 'NUMERIC' && item.get('formula_Type')?.value === 'MEAN'); }
  usesUncertainty(item: FormGroup): boolean { return item.get('result_Type')?.value === 'TRUENESS_BIAS'; }
  showLower(item: FormGroup): boolean { return ['MIN', 'RANGE'].includes(item.get('acceptance_Type')?.value); }
  showUpper(item: FormGroup): boolean { return ['MAX', 'RANGE'].includes(item.get('acceptance_Type')?.value); }
  showMaxDifference(item: FormGroup): boolean { return ['ABS_DIFFERENCE', 'PERCENT_DIFFERENCE', 'STD_DEV'].includes(item.get('acceptance_Type')?.value); }

  save(): void {
    this.reorder();
    if (this.form.invalid || this.items.length === 0) { this.form.markAllAsTouched(); return; }
    if (this.saving) return;
    this.saving = true;
    this.service.saveTemplate(this.form.getRawValue()).pipe(finalize(() => this.saving = false)).subscribe({
      next: x => { this.alert.SuccessMessage('الگوی کنترل میانی با موفقیت ویرایش شد'); this.router.navigate(['/ic/template/edit', x.id]); },
      error: e => this.alert.ErrorMessage(e?.error?.message || 'ثبت الگو با خطا مواجه شد'),
    });
  }
  cancel(): void { this.router.navigate(['/ic/template']); }
}
